import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { FollowUp } from '../../../domain/follow-up';
import { LoadingState } from '../../../domain/loading-state';
import { Property } from '../../../domain/property';
import { WorkflowType } from '../../../domain/enums/workflow-type';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';
import { PhobosTaskInstanceService } from '../../../services/phobos-task-instance.service';
import { FollowUpStoreService } from '../../../services/stores/follow-up-store.service';
import { LoadingOverlayStoreService } from '../../../services/stores/loading-overlay-store.service';
import { PropertyStoreService } from '../../../services/stores/property-store.service';
import { TaskUtils } from '../../../util/task-utils';

@Component({
   selector: 'app-my-follow-ups',
   standalone: true,
   imports: [
      CommonModule,
      TableModule,
      ProgressSpinnerModule,
      ButtonModule,
      CheckboxModule,
      ToastModule,
      DatePropertyTimezonePipe
   ],
   templateUrl: './my-follow-ups.component.html',
   styleUrls: ['./my-follow-ups.component.scss']
})
export class MyFollowUpsComponent implements OnInit {
   followUps: FollowUp[] = [];
   selectedFollowUps: FollowUp[] = [];

   isLoading = false;
   isCompleting = false;

   private selectedProperty: Property | undefined;

   constructor(
      private followUpStore: FollowUpStoreService,
      private propStore: PropertyStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private router: Router,
      private formTaskService: PhobosTaskInstanceService,
      private messageService: MessageService,
      private destroyRef: DestroyRef
   ) {
      this.propStore.currentProperty
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((value) => {
            this.selectedProperty = value;
         });
   }

   ngOnInit(): void {
      this.isLoading = true;

      this.followUpStore.followUps
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((followUps) => {
            this.followUps = followUps;
            this.isLoading = false;
            this.isCompleting = false;
         });
   }

   completeSelectedFollowUps(): void {
      if (this.selectedFollowUps.length === 0) {
         return;
      }

      const idArray: number[] = [];
      const formIdArray: number[] = [];

      for (const item of this.selectedFollowUps) {
         if (item.type === WorkflowType.Form) {
            formIdArray.push(item.id);
         } else {
            idArray.push(item.id);
         }
      }

      const loadingState = new LoadingState();
      loadingState.FollowUps = true;

      this.isCompleting = true;
      this.loadingOverlayStore.setLoadingState(loadingState);

      if (idArray.length > 0) {
         this.followUpStore.completeFollowUps(idArray).subscribe({
            next: (result) => {
               if (result > 0) {
                  this.followUpStore.refreshStore();

                  this.messageService.add({
                     severity: 'success',
                     summary: 'Follow Ups Completed',
                     detail: `${idArray.length} follow-up(s) marked as completed`
                  });
               }

               this.isCompleting = false;
            },
            error: () => {
               this.messageService.add({
                  severity: 'error',
                  summary: 'Error Completing Follow Ups',
                  detail: 'An error occurred while completing standard follow-ups.'
               });

               this.isCompleting = false;
            }
         });
      }

      if (formIdArray.length > 0) {
         this.formTaskService.completeFollowUps(formIdArray).subscribe({
            next: (result) => {
               if (result > 0) {
                  this.followUpStore.refreshStore();

                  this.messageService.add({
                     severity: 'success',
                     summary: 'Follow Ups Completed',
                     detail: `${formIdArray.length} follow-up(s) marked as completed`
                  });

                  this.isCompleting = false;
               }
            },
            error: () => {
               this.messageService.add({
                  severity: 'error',
                  summary: 'Error Completing Form Follow Ups',
                  detail: 'An error occurred while completing form follow-ups.'
               });

               this.isCompleting = false;
            }
         });
      }
   }

   async goToTask(followUp: FollowUp, event: MouseEvent): Promise<void> {
      if (!this.selectedProperty) {
         return;
      }

      event.preventDefault();

      await TaskUtils.goToForm(followUp, this.router, this.selectedProperty);
   }
}
